import React, { useState } from "react";
import { ArrowUpRight, Mail, Lock, Eye, EyeOff, CheckCircle, Leaf } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import userAPI from "../../api/UserApi";
import { showSuccess } from "../../utils/toastUtils";
import { setAccessToken } from "../../api/axiosInstance";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import Breadcrump from "../../components/Breadcrump";

const LoginCanvas = () => {
  useAlphabeticInput();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", rememberMe: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await userAPI.login({
        email: formData.email,
        password: formData.password,
      });

      if (res.code === 200) {
        localStorage.setItem(
          "token",
          JSON.stringify({ email: res.data.email, username: res.data.username })
        );
        localStorage.setItem("accessToken", res.data.accessToken);
        setAccessToken(res.data.accessToken);
        showSuccess(res.message || "Logged in successfully");
        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (

    <>
      <Breadcrump title="Login" />
      <div className="login-root w-full flex flex-col lg:flex-row lg:h-screen lg:overflow-hidden overflow-x-hidden bg-[var(--color-bg)]">

        <style>{`
        :root {
          --color-primary: #A6472E;
          --color-primary-dark: #7A2F1E;
          --color-primary-light: #C9694C;
          --color-secondary: #4B3226;
          --color-secondary-light: #6E4A38;
          --color-accent-green: #4A5B3A;
          --color-accent-green-light: #7C8A63;
          --color-accent-gold: #B08D57;
          --color-accent-gold-light: #D4B483;
          --color-tan: #C9A26D;
          --color-tan-light: #E8D6B3;
          --color-bg: #FFFDF8;
          --color-bg-alt: #F6EFE2;
          --color-white: #FFFFFF;
          --color-black: #1A1310;
          --color-text: #2E211A;
          --color-text-muted: #6B5D53;
          --color-border: #E5D9C3;
          --shadow-sm: 0 1px 3px rgba(74, 47, 30, 0.12);
          --shadow-md: 0 4px 12px rgba(74, 47, 30, 0.16);
          --shadow-lg: 0 10px 30px rgba(74, 47, 30, 0.2);
          --font-heading: 'Bebas Neue', cursive;
          --font-body: 'Space Grotesk', sans-serif;
        }

        @keyframes pulseGlow {
          0% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.05); opacity: 0.7; }
          100% { transform: scale(1); opacity: 0.4; }
        }

        .lg-panel {
          width: 50%;
          position: relative;
          height: 100vh;
          flex-shrink: 0;
        }

        .form-panel {
          width: 50%;
          // height: 100vh;
          // overflow-y: auto;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          background: var(--color-bg);
        }

        .spice-input {
          background: transparent;
          border: none;
          border-bottom: 1.5px solid var(--color-border);
          padding: 12px 28px 12px 0;
          width: 100%;
          font-family: var(--font-body);
          font-size: 1rem;
          color: var(--color-text);
          transition: border-color 0.3s ease, padding-left 0.3s ease;
        }
        .spice-input:focus {
          outline: none;
          border-color: var(--color-primary);
        }
        .spice-input::placeholder { color: transparent; }

        .spice-label {
          position: absolute; left: 0; top: 12px;
          font-family: var(--font-body);
          font-size: 0.62rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.18em;
          color: var(--color-text-muted);
          pointer-events: none;
          transition: all 0.28s ease;
        }
        .spice-input:focus ~ .spice-label,
        .spice-input:not(:placeholder-shown) ~ .spice-label {
          top: -11px;
          font-size: 0.54rem;
          color: var(--color-accent-gold);
          letter-spacing: 0.22em;
        }

        .spice-tab {
          padding-bottom: 12px;
          font-family: var(--font-body);
          font-size: 0.62rem; font-weight: 700;
          letter-spacing: 0.2em; text-transform: uppercase;
          background: none; border: none; cursor: pointer;
          position: relative; transition: color 0.25s ease;
          text-decoration: none; display: inline-block;
        }

        .spice-submit {
          width: 100%;
          display: flex; align-items: center; justify-content: space-between;
          padding: 15px 22px;
          background: var(--color-primary); color: var(--color-white);
          border: none; border-radius: var(--radius-sm); cursor: pointer;
          box-shadow: var(--shadow-md);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .spice-submit:hover { 
          background: var(--color-primary-dark); 
          transform: translateY(-2px); 
          box-shadow: var(--shadow-lg); 
        }
        .spice-submit-label {
          font-family: var(--font-body);
          font-size: 0.65rem; font-weight: 700;
          letter-spacing: 0.22em; text-transform: uppercase;
        }
        .spice-submit-icon {
          width: 34px; height: 34px; border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.25);
          display: flex; align-items: center; justify-content: center;
          transition: all 0.3s ease; flex-shrink: 0;
        }
        .spice-submit:hover .spice-submit-icon {
          background: var(--color-accent-gold);
          border-color: var(--color-accent-gold);
          transform: rotate(45deg);
        }

        .spice-check {
          width: 16px; height: 16px; border-radius: var(--radius-sm);
          border: 1.5px solid var(--color-border);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: all 0.2s ease; cursor: pointer;
        }
        .spice-check.on { background: var(--color-primary); border-color: var(--color-primary); }

        .gold-seam {
          position: absolute; left: 0; top: 0; bottom: 0;
          width: 1px;
          background: linear-gradient(to bottom, transparent, var(--color-accent-gold), transparent);
          opacity: 0.45;
        }

        .animated-bg-glow {
          animation: pulseGlow 8s infinite ease-in-out;
        }

        @media (max-width: 1023px) {
          .lg-panel { display: none !important; }
          .form-panel { width: 100% !important; height: auto !important; min-height: 100vh; }
          .mobile-logo { display: block !important; }
        }
      `}</style>

        {/* ══ LEFT: Brand Visual ══ */}
        <div className="lg-panel">
          <img
            src="/images/loginimg.jpeg"
            alt="Spice Market"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div className="animated-bg-glow" style={{ position: "absolute", inset: 0, background: "var(--color-primary-dark)", mixBlendMode: "multiply", opacity: 0.65 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(26,19,16,0.85) 0%, transparent 60%)" }} />

          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "52px 56px", zIndex: 10 }}>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
              </div>
            </div>

            <div style={{ maxWidth: 380, color: "var(--color-white)" }}>
              <div style={{ width: 36, height: 2, background: "var(--color-accent-gold)", borderRadius: 1, marginBottom: 22 }} />
              <h1 style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(3rem, 5vw, 4.5rem)", lineHeight: 0.95, letterSpacing: "0.02em", marginBottom: 18 }}>
                Taste the{" "}
                <span style={{ color: "var(--color-accent-gold)", display: "block", lineHeight: 1.1 }}>
                  Tradition.
                </span>
              </h1>
              <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "1rem", color: "var(--color-tan-light)", lineHeight: 1.75 }}>
                Sign in to access your curated dry fruits collection, track orders, and discover rare blends sourced directly from Indian farms.
              </p>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
            </div>
          </div>
        </div>

        {/* ══ RIGHT: Form ══ */}
        <div className="form-panel">

          <div className="lg-grain absolute inset-0 pointer-events-none" style={{ opacity: 0.55 }} />
          <div className="gold-seam" />

          <div style={{ width: "100%", maxWidth: 420, margin: "0 auto", padding: "48px 32px", position: "relative", zIndex: 10 }}>

            <div style={{ marginBottom: 28 }}>
              <h2 style={{ fontFamily: "var(--font-heading)", fontSize: "2.5rem", color: "var(--color-text)", letterSpacing: "0.02em", lineHeight: 1 }}>
                Sign In
              </h2>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 28, borderBottom: "1px solid var(--color-border)", marginBottom: 32 }}>
              <button
                className="spice-tab"
                style={{ color: "var(--color-text)" }}
              >
                Sign In
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "var(--color-primary)", borderRadius: 1 }} />
              </button>
              <Link to="/users/register" className="spice-tab" style={{ color: "var(--color-text-muted)" }}>
                Register
              </Link>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 28 }}>

              {/* Email */}
              <div style={{ position: "relative", paddingBottom: 4 }}>
                <input type="email" name="email" id="email" className="spice-input" placeholder=" " value={formData.email} onChange={handleChange} required />
                <label htmlFor="email" className="spice-label">Email Address</label>
                <Mail size={15} style={{ position: "absolute", right: 0, top: 14, color: "var(--color-text-muted)" }} />
                {errors.email && (
                  <span style={{ display: "block", marginTop: 6, fontSize: "0.7rem", color: "var(--color-primary)" }}>{errors.email}</span>
                )}
              </div>

              {/* Password */}
              <div style={{ position: "relative", paddingBottom: 4 }}>
                <input type={showPassword ? "text" : "password"} name="password" id="password" className="spice-input" style={{ paddingRight: 28 }} placeholder=" " value={formData.password} onChange={handleChange} required />
                <label htmlFor="password" className="spice-label">Password</label>
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 0, top: 14, background: "none", border: "none", cursor: "pointer", color: "var(--color-text-muted)", padding: 0, display: "flex", alignItems: "center" }}>
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                {errors.password && (
                  <span style={{ display: "block", marginTop: 6, fontSize: "0.7rem", color: "var(--color-primary)" }}>{errors.password}</span>
                )}
              </div>
              {errors.general && (
                <span style={{ display: "block", fontSize: "0.72rem", color: "var(--color-primary)", marginTop: -14 }}>{errors.general}</span>
              )}

              {/* Remember + Forgot */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {/* <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <div className={`spice-check ${formData.rememberMe ? "on" : ""}`} onClick={() => setFormData({ ...formData, rememberMe: !formData.rememberMe })}>
                    {formData.rememberMe && <CheckCircle size={10} color="#fff" />}
                  </div>
                  <input type="checkbox" name="rememberMe" style={{ display: "none" }} checked={formData.rememberMe} onChange={handleChange} />
                  <span style={{ fontFamily: "var(--font-body)", fontSize: "0.58rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-text)" }}>Remember me</span>
                </label> */}
                <Link to="/users/forgot-password" style={{ fontFamily: "var(--font-body)", fontSize: "0.58rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-text-muted)", textDecoration: "none", transition: "color 0.2s ease" }}
                  onMouseEnter={e => e.target.style.color = "var(--color-primary)"}
                  onMouseLeave={e => e.target.style.color = "var(--color-text-muted)"}
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Submit */}
              <div style={{ paddingTop: 4 }}>
                <button type="submit" className="spice-submit" disabled={loading} style={{ opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
                  <span className="spice-submit-label">
                    {loading ? "Please wait..." : "Sign In"}
                  </span>
                  <div className="spice-submit-icon">
                    <ArrowUpRight size={15} />
                  </div>
                </button>
              </div>

            </form>

            {/* Register link */}
            <Link
              to="/users/register"
              className="mt-3"
              style={{ padding: "10px 16px", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", background: "var(--color-white)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "var(--font-body)", fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-text)", cursor: "pointer", transition: "all 0.22s ease", textDecoration: "none" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--color-primary)"; e.currentTarget.style.color = "var(--color-primary)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--color-border)"; e.currentTarget.style.color = "var(--color-text)"; }}
            >
              Create an Account
            </Link>

          </div>
        </div>
      </div>
    </>
  );
};

export default LoginCanvas;